/**
 * ActivityTracker — Frontend event tracking service
 * 
 * Tracks page views, outfit views, clicks, searches, and session heartbeats.
 * Batches events and sends them to the backend every 30 seconds.
 * Detects idle state (5 min no activity) and pauses heartbeats.
 */

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';
const FLUSH_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_INTERVAL = 60000; // 60 seconds
const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

interface ActivityEvent {
  eventId: string;
  eventType: string;
  userId?: string;
  sessionId?: string;
  timestamp: string;
  properties: Record<string, any>;
  device: {
    type: 'mobile' | 'desktop' | 'tablet';
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
  };
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function getDeviceType(): 'mobile' | 'desktop' | 'tablet' {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getDeviceInfo() {
  return {
    type: getDeviceType(),
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
}

class ActivityTrackerService {
  private eventQueue: ActivityEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivityTime: number = Date.now();
  private isIdle: boolean = false;
  private isInitialized: boolean = false;
  private viewedOutfits: Set<string> = new Set(); // Dedup outfit views per session
  private currentPage: string = '';

  /**
   * Initialize the tracker — call once from App.tsx
   */
  init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Start flush timer
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL);

    // Start heartbeat timer
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL);

    // Idle detection
    const markActive = () => {
      this.lastActivityTime = Date.now();
      if (this.isIdle) {
        this.isIdle = false;
        console.log('[ActivityTracker] User active again');
      }
    };

    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(evt => {
      window.addEventListener(evt, markActive, { passive: true });
    });

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush(true));

    // Flush on visibility change (tab hidden)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush(true);
      }
    });

    console.log('[ActivityTracker] Initialized');
  }

  /**
   * Get userId and sessionId from localStorage/session
   */
  private getIdentifiers(): { userId?: string; sessionId?: string } {
    try {
      const sessionStr = localStorage.getItem('enhanced_session');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        return {
          userId: session.userId || session.user?.id,
          sessionId: session.sessionId,
        };
      }
    } catch {}
    return {};
  }

  /**
   * Create an event object
   */
  private createEvent(eventType: string, properties: Record<string, any> = {}): ActivityEvent {
    const { userId, sessionId } = this.getIdentifiers();
    return {
      eventId: generateId(),
      eventType,
      userId,
      sessionId,
      timestamp: new Date().toISOString(),
      properties,
      device: getDeviceInfo(),
    };
  }

  /**
   * Queue an event for batched sending
   */
  private queueEvent(event: ActivityEvent): void {
    this.eventQueue.push(event);
    // Auto-flush if queue gets large
    if (this.eventQueue.length >= 50) {
      this.flush();
    }
  }

  // ─── Public Tracking Methods ─────────────────────────────────────────

  /**
   * Track a page view
   */
  trackPageView(page: string): void {
    if (page === this.currentPage) return; // Dedup same page
    this.currentPage = page;
    this.queueEvent(this.createEvent('page_view', { page }));
  }

  /**
   * Track outfit card visible in viewport (call from IntersectionObserver)
   */
  trackOutfitView(outfitId: string): void {
    if (this.viewedOutfits.has(outfitId)) return; // Dedup within session
    this.viewedOutfits.add(outfitId);
    this.queueEvent(this.createEvent('outfit_view', { outfitId }));
  }

  /**
   * Track outfit card clicked (navigated to detail)
   */
  trackOutfitClick(outfitId: string): void {
    this.queueEvent(this.createEvent('outfit_click', { outfitId }));
  }

  /**
   * Track outfit rated
   */
  trackOutfitRate(outfitId: string, rating: number): void {
    this.queueEvent(this.createEvent('outfit_rate', { outfitId, value: rating }));
  }

  /**
   * Track outfit liked/unliked
   */
  trackOutfitLike(outfitId: string, liked: boolean): void {
    this.queueEvent(this.createEvent('outfit_like', { outfitId, action: liked ? 'like' : 'unlike' }));
  }

  /**
   * Track search query
   */
  trackSearch(query: string, resultCount: number, isAI: boolean): void {
    this.queueEvent(this.createEvent('search_query', { query, resultCount, isAI }));
  }

  /**
   * Track search result clicked
   */
  trackSearchResultClick(outfitId: string, query: string, position: number): void {
    this.queueEvent(this.createEvent('search_result_click', { outfitId, query, position }));
  }

  /**
   * Track filter change
   */
  trackFilterChange(filterType: string, value: string[]): void {
    this.queueEvent(this.createEvent('filter_change', { filterType, value }));
  }

  /**
   * Track upload start
   */
  trackUploadStart(): void {
    this.queueEvent(this.createEvent('upload_start', {}));
  }

  /**
   * Track upload complete
   */
  trackUploadComplete(outfitId: string): void {
    this.queueEvent(this.createEvent('upload_complete', { outfitId }));
  }

  // ─── Internal Methods ────────────────────────────────────────────────

  /**
   * Send heartbeat if user is active
   */
  private sendHeartbeat(): void {
    const timeSinceActivity = Date.now() - this.lastActivityTime;
    if (timeSinceActivity > IDLE_TIMEOUT) {
      if (!this.isIdle) {
        this.isIdle = true;
        console.log('[ActivityTracker] User idle');
      }
      return; // Don't send heartbeat when idle
    }
    this.queueEvent(this.createEvent('session_heartbeat', { page: this.currentPage }));
  }

  /**
   * Flush event queue to backend
   */
  private async flush(useBeacon: boolean = false): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    const payload = JSON.stringify({
      events,
      batchId: generateId(),
      clientTimestamp: new Date().toISOString(),
    });

    const url = `${API_BASE_URL}/analytics/events`;

    // Use fetch with credentials: 'omit' to avoid CORS credential issues.
    // sendBeacon can trigger CORS errors when service workers intercept requests.
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'omit',
        mode: 'cors',
      });
    } catch {
      // Silently fail — analytics should never break the app
    }
  }

  private async sendViaFetch(url: string, payload: string): Promise<void> {
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true, // Allows request to outlive the page
        credentials: 'omit', // Public endpoint — no cookies/auth needed
        mode: 'cors',
      });
    } catch (err) {
      // Silently fail — analytics should never break the app
      console.warn('[ActivityTracker] Flush failed:', err);
    }
  }

  /**
   * Cleanup — call on unmount
   */
  destroy(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.flush(true);
    this.isInitialized = false;
  }
}

// Singleton instance
const activityTracker = new ActivityTrackerService();
export default activityTracker;
